import { useEffect, useMemo, useState } from "react";
import {
  useQuery,
  getInviteCodes,
  generateInviteCode,
  deactivateInviteCode,
} from "wasp/client/operations";
import {
  Copy,
  Check,
  QrCode,
  Plus,
  Ban,
  Share2,
  Download,
} from "lucide-react";
import QRCode from "qrcode";

type Props = {
  locationId: string;
  locationCode?: string | null;
};

export default function JoinSharePanel({ locationId, locationCode }: Props) {
  const joinPath = locationCode ? `/locations/${locationCode}` : "";
  const joinUrl = useMemo(() => {
    if (!joinPath || typeof window === "undefined") return joinPath;
    return `${window.location.origin}${joinPath}`;
  }, [joinPath]);

  const { data, refetch, isLoading } = useQuery(
    getInviteCodes,
    { locationId, activeOnly: true },
    { enabled: !!locationId },
  );
  const codes = (data as any[]) || [];

  const [qrSrc, setQrSrc] = useState("");
  const [copied, setCopied] = useState<"url" | "code" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selectedCode, setSelectedCode] = useState<string>("");
  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  useEffect(() => {
    if (!joinUrl) return;
    QRCode.toDataURL(joinUrl, {
      width: 512,
      margin: 2,
      color: { dark: "#0f172a", light: "#ffffff" },
    })
      .then(setQrSrc)
      .catch(() => setQrSrc(""));
  }, [joinUrl]);

  useEffect(() => {
    if (!selectedCode && codes[0]?.code) {
      setSelectedCode(codes[0].code);
    }
  }, [codes, selectedCode]);

  async function copy(text: string, kind: "url" | "code") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setError("Could not copy — select the text manually");
    }
  }

  async function onShare() {
    if (!joinUrl) return;
    setError("");
    const text = selectedCode
      ? `Join this building:\n${joinUrl}\nAccess code: ${selectedCode}`
      : `Join this building:\n${joinUrl}`;
    try {
      if (canNativeShare) {
        await navigator.share({
          title: "Building join link",
          text,
          url: joinUrl,
        });
        return;
      }
      await copy(text, "url");
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setError(e?.message || "Share cancelled");
      }
    }
  }

  function onDownloadQr() {
    if (!qrSrc) return;
    const a = document.createElement("a");
    a.href = qrSrc;
    a.download = `join-${locationCode || "building"}-qr.png`;
    a.click();
  }

  async function onGenerate() {
    setBusy(true);
    setError("");
    try {
      const created = await generateInviteCode({
        locationId,
        maxUses: 500,
      });
      setSelectedCode((created as any).code);
      await refetch();
    } catch (e: any) {
      setError(e?.message || "Could not generate code");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-3xl border border-neutral-200 bg-white p-4 sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <QrCode className="h-5 w-5 shrink-0 text-sky-700" />
            <h2 className="text-lg font-semibold">Join link &amp; QR</h2>
          </div>
          <p className="mt-1 text-sm text-neutral-600">
            Point building QR stickers at this URL. Tenants then enter a code or
            request access (per platform toggles).
          </p>
        </div>
        <button
          type="button"
          disabled={busy || !locationId}
          onClick={onGenerate}
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-neutral-900 px-4 text-sm font-semibold text-white disabled:opacity-50 sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          {busy ? "Generating…" : "New access code"}
        </button>
      </div>

      {!locationCode && (
        <p className="mt-3 text-sm text-amber-800">
          This location needs a location code before a join URL can be shared.
        </p>
      )}

      {joinUrl && (
        <div className="mt-4 grid gap-4 sm:grid-cols-[220px_1fr]">
          <div className="mx-auto flex w-full max-w-[240px] flex-col items-center rounded-2xl border border-neutral-100 bg-neutral-50 p-3">
            {qrSrc ? (
              <img
                src={qrSrc}
                alt="Join QR code"
                className="h-48 w-48 touch-manipulation sm:h-52 sm:w-52"
                width={208}
                height={208}
              />
            ) : (
              <div className="flex h-48 w-48 items-center justify-center text-sm text-neutral-400 sm:h-52 sm:w-52">
                QR…
              </div>
            )}
            <button
              type="button"
              disabled={!qrSrc}
              onClick={onDownloadQr}
              className="mt-2 inline-flex min-h-10 w-full items-center justify-center gap-1.5 rounded-xl border border-neutral-200 bg-white px-3 text-xs font-semibold text-neutral-800 disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" />
              Download QR
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Join URL
              </p>
              <code className="mt-1 block break-all rounded-2xl bg-neutral-100 px-3 py-3 text-sm leading-relaxed">
                {joinUrl}
              </code>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                <button
                  type="button"
                  onClick={() => copy(joinUrl, "url")}
                  className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-2xl border border-neutral-200 px-3 text-sm font-semibold"
                >
                  {copied === "url" ? (
                    <Check className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  Copy link
                </button>
                <button
                  type="button"
                  onClick={onShare}
                  className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-2xl bg-sky-700 px-3 text-sm font-semibold text-white"
                >
                  <Share2 className="h-4 w-4" />
                  {canNativeShare ? "Share" : "Copy share text"}
                </button>
              </div>
            </div>

            {selectedCode && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Active code to share
                </p>
                <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                  <code className="rounded-2xl bg-sky-50 px-4 py-3 text-center text-2xl font-bold tracking-[0.25em] text-sky-900 sm:text-left sm:text-lg sm:tracking-widest">
                    {selectedCode}
                  </code>
                  <button
                    type="button"
                    onClick={() => copy(selectedCode, "code")}
                    className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-2xl border border-neutral-200 px-3 text-sm font-semibold"
                  >
                    {copied === "code" ? (
                      <Check className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    Copy code
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-6">
        <h3 className="text-sm font-semibold text-neutral-800">Active codes</h3>
        {isLoading && (
          <p className="mt-2 text-sm text-neutral-500">Loading codes…</p>
        )}
        <ul className="mt-2 space-y-2">
          {codes.map((c) => (
            <li
              key={c.id}
              className="flex flex-col gap-2 rounded-2xl border border-neutral-200 px-3 py-3 text-sm sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
            >
              <button
                type="button"
                className="min-h-11 text-left font-mono text-base font-semibold tracking-wider sm:min-h-0 sm:text-sm"
                onClick={() => setSelectedCode(c.code)}
              >
                {c.code}
                {c.unitNumber ? (
                  <span className="ml-2 font-sans font-normal text-neutral-500">
                    unit {c.unitNumber}
                  </span>
                ) : null}
                <span className="ml-2 font-sans font-normal text-neutral-400">
                  {c.currentUses}/{c.maxUses} uses
                </span>
              </button>
              <button
                type="button"
                className="inline-flex min-h-11 items-center justify-center gap-1 rounded-xl px-3 text-sm text-red-700 hover:bg-red-50 sm:min-h-9 sm:justify-start sm:text-xs"
                onClick={async () => {
                  await deactivateInviteCode({ id: c.id });
                  if (selectedCode === c.code) setSelectedCode("");
                  await refetch();
                }}
              >
                <Ban className="h-3.5 w-3.5" />
                Deactivate
              </button>
            </li>
          ))}
          {!isLoading && codes.length === 0 && (
            <li className="text-sm text-neutral-500">
              No active codes — generate one for the QR sticker.
            </li>
          )}
        </ul>
      </div>
    </section>
  );
}
